import type { Method } from '@/lib/types/precognition';
import type { FormDataType } from '@inertiajs/core';
import { type InertiaForm, useForm as inertiaUseForm } from '@inertiajs/svelte';
import axios, { AxiosError } from 'axios';
import type { Writable } from 'svelte/store';

const formIntervalMap: Record<string, number> = {};
const lastPrecognitionSent: Record<string, string> = {};

export const useForm = <T extends FormDataType<T>>(data: T, method: Method, url: string, debounce: number = 300): Writable<InertiaForm<T>> => {
    const form = inertiaUseForm(data);
    const formIdentifier = `${method}-${url}`;

    form.subscribe((inertiaForm) => {
        if (formIntervalMap[formIdentifier]) {
            clearTimeout(formIntervalMap[formIdentifier]);
        }

        formIntervalMap[formIdentifier] = setTimeout(async () => {
            if (inertiaForm.isDirty) {
                const data = inertiaForm.data();
                const dataJson = JSON.stringify(data);

                if (lastPrecognitionSent[formIdentifier] === dataJson) {
                    return;
                }

                lastPrecognitionSent[formIdentifier] = dataJson;

                try {
                    await axios[method](
                        url,
                        {
                            ...data,
                            precognitive: 'true',
                        },
                        {
                            headers: {
                                Precognition: 'true',
                                Accept: 'application/json',
                            },
                        },
                    );

                    inertiaForm.clearErrors();
                } catch (e) {
                    const err = e as AxiosError<{ errors: Record<string, string[]> }>;

                    if (err.status === 422 && err.response) {
                        // Build errors object from response
                        const errorObj: Record<string, string> = {};
                        for (const [field, errorsArr] of Object.entries(err.response.data.errors)) {
                            errorObj[field] = errorsArr[0];
                        }
                        // Directly replace the entire errors object (not merge)
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        inertiaForm.setStore('errors', errorObj);
                    } else {
                        throw e;
                    }
                }
            }
        }, debounce);
    });

    return form;
};
